import { useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedAccount, setSelectedCompany, setSelectedDate, setSelectedFME, setSelectedPercentCovered, setSelectedStatus } from '../../../../../features/financeReceiptSlice';
import { Box } from '@mui/material';
import FilterComponent from '../../../../ReusableFIlterComponent/FilterComponent';

export const ReceiptFilterItems = ({ clearSelectedReceiptNumber }: any) => {
  const { receiptData, selectedCompany, selectedFME, selectedDate, selectedStatus, selectedAccount, selectedPercentCovered } = useSelector((state: any) => state.receipts);
  const dispatch = useDispatch();

  const [searchCompany, setSearchCompany] = useState("");
  const [searchFME, setSearchFME] = useState("");
  const [searchStatus, setSearchStatus] = useState("");
  const [searchAccount, setSearchAccount] = useState("");
  const [searchPercentCovered, setSearchPercentCovered] = useState("");

  // Extract unique company names from nested fmes array
  const AccountData = useMemo(() => {
    if (!receiptData || receiptData.length === 0) return [];
    
    const allAccounts = receiptData?.flatMap((receipt: any) => 
      receipt?.fmes?.map((fme: any) => ({
        account_id: fme?.account_id
      })) || []
    );
    
    // Remove duplicates based on company name
    const uniqueCompanies = allAccounts?.filter((company: any, index: number, self: any[]) =>
      index === self.findIndex((c: any) => c?.account_id === company?.account_id)
    );
    
    return uniqueCompanies;
  }, [receiptData]);

  // Extract unique fme ids
  const fmeIdData = useMemo(() => {
    if (!receiptData || receiptData.length === 0) return []

      const allIds = receiptData?.flatMap(
          (receipt: any) =>
              receipt?.fmes?.map((fme: any) => ({
                  fme_id: fme?.fme_id,
              })) || []
      )

      // Remove duplicates based on fme ids
      const uniqueIds = allIds?.filter(
          (fme: any, index: number, self: any[]) =>
              index === self.findIndex((c: any) => c?.fme_id === fme?.fme_id)
      )
      return uniqueIds
  }, [receiptData])

  const filterConfig = [
    {
      key: "company",
      label: "Select a Company",
      searchTerm: searchCompany,
      onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => setSearchCompany(e.target.value),
      selectedOption: selectedCompany,
      filterName: "finance_company_name",
      data: receiptData,
      onSelectionChange: (updatedSelection: any) => dispatch(setSelectedCompany(updatedSelection)),
      count: selectedCompany && selectedCompany.length,
      colorSelected:
        selectedCompany !== null && selectedCompany.length > 0
          ? "#9C27B0"
          : "#000000DE",
      multiSelect: true,
      showSearch: true,
    },
    {
      key: "fme",
      label: "Select FME ID",
      searchTerm: searchFME,
      onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => setSearchFME(e.target.value),
      selectedOption: selectedFME,
      filterName: "fme_id",
      data: fmeIdData,
      onSelectionChange: (updatedSelection: any) => dispatch(setSelectedFME(updatedSelection)),
      count: selectedFME && selectedFME.length,
      colorSelected:
        selectedFME !== null && selectedFME.length > 0
          ? "#9C27B0"
          : "#000000DE",
      multiSelect: true,
      showSearch: true,
    },
    {
      key: "date",
      label: "Date",
      selectedOption: selectedDate,
      filterName: "date",
      data: receiptData,
      onSelectionChange: (updatedSelection: any) => dispatch(setSelectedDate(updatedSelection)),
      count: 0,
      colorSelected:
        selectedDate !== null && selectedDate.length > 0
          ? "#9C27B0"
          : "#000000DE",
      multiSelect: true,
      isDateRangeFilter: true,
    },
    {
      key: "Status",
      label: "Select Status",
      searchTerm: searchStatus,
      onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => setSearchStatus(e.target.value),
      selectedOption: selectedStatus,
      filterName: "receipt_status",
      data: [{ receipt_status: "Final" }, { receipt_status: "Not Final" }],
      onSelectionChange: (updatedSelection: any) => dispatch(setSelectedStatus(updatedSelection)),
      count: selectedStatus && selectedStatus.length,
      colorSelected:
        selectedStatus !== null && selectedStatus.length > 0
          ? "#9C27B0"
          : "#000000DE",
      multiSelect: false,
      showSearch: false,
    },
    {
      key: "account",
      label: "Select an Account",
      searchTerm: searchAccount,
      onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => setSearchAccount(e.target.value),
      selectedOption: selectedAccount,
      filterName: "account_id",
      data: AccountData,
      onSelectionChange: (updatedSelection: any) => dispatch(setSelectedAccount(updatedSelection)),
      count: selectedAccount && selectedAccount?.length,
      colorSelected:
        selectedAccount !== null && selectedAccount?.length > 0
          ? "#9C27B0"
          : "#000000DE",
      multiSelect: true,
      showSearch: true,
    },
    {
      key: "% Covered",
      label: "Percentage Covered",
      searchTerm: searchPercentCovered,
      onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => setSearchPercentCovered(e.target.value),
      selectedOption: selectedPercentCovered,
      filterName: "total_covered",
      data: [
        { total_covered: "Below 30%" },
        { total_covered: "Between 30% and 70%" },
        { total_covered: "Over 70%" }
      ],
      onSelectionChange: (updatedSelection: any) => dispatch(setSelectedPercentCovered(updatedSelection)),
      count: selectedPercentCovered && selectedPercentCovered.length,
      colorSelected:
        selectedPercentCovered !== null && selectedPercentCovered.length > 0
          ? "#9C27B0"
          : "#000000DE",
      multiSelect: true,
      showSearch: false, // No search needed for only 3 options
    }
  ];

  const clearFilters = () => {
    setSearchCompany("");
    setSearchFME("");
    setSearchStatus("");
    setSearchAccount("");
    setSearchPercentCovered("");
    clearSelectedReceiptNumber();
    dispatch(setSelectedCompany([]));
    dispatch(setSelectedFME([]));
    dispatch(setSelectedDate([]));
    dispatch(setSelectedStatus([]));
    dispatch(setSelectedAccount([]));
    dispatch(setSelectedPercentCovered([]));
  }

  return (
    <Box>
      <FilterComponent filterConfig={filterConfig} clearFilters={clearFilters} />
    </Box>
  )
}
